import { Alert, Button, Field, Modal } from '@catering/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FormEvent, type JSX } from 'react';
import { type Resolver, useForm } from 'react-hook-form';

import {
  DEFAULT_ALLERGEN_FORM_VALUES,
  type AllergenFormFields,
  type AllergenFormValues,
  allergenFormSchema,
} from '../../../menus/category-form-schema';

export interface AllergenFormProps {
  title: string;
  submitLabel: string;
  mode: 'create' | 'edit';
  defaultValues?: AllergenFormFields;
  submitting: boolean;
  formError?: string;
  onCancel: () => void;
  onSubmit: (values: AllergenFormValues) => Promise<void>;
}

export function AllergenForm({
  title,
  submitLabel,
  mode,
  defaultValues,
  submitting,
  formError,
  onCancel,
  onSubmit,
}: AllergenFormProps): JSX.Element {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AllergenFormFields, unknown, AllergenFormValues>({
    resolver: zodResolver(allergenFormSchema) as unknown as Resolver<
      AllergenFormFields,
      unknown,
      AllergenFormValues
    >,
    defaultValues: defaultValues ?? DEFAULT_ALLERGEN_FORM_VALUES,
  });

  function handleFormSubmit(event: FormEvent): void {
    void handleSubmit((values) => onSubmit(values))(event);
  }

  return (
    <Modal open onClose={onCancel} title={title} className="max-w-md">
      <form onSubmit={handleFormSubmit} className="flex flex-col gap-4" noValidate>
        {formError !== undefined ? <Alert tone="danger">{formError}</Alert> : null}

        <Field
          label="Código"
          required
          disabled={mode === 'edit'}
          {...register('code')}
          {...maybeError(errors.code?.message)}
        />
        <Field
          label="Nombre"
          required
          {...register('name')}
          {...maybeError(errors.name?.message)}
        />
        <Field label="Ícono" {...register('icon')} {...maybeError(errors.icon?.message)} />
        <Field
          label="Orden de presentación"
          type="number"
          min={0}
          {...register('displayOrder')}
          {...maybeError(errors.displayOrder?.message)}
        />

        <div className="mt-2 flex items-center gap-3">
          <Button type="submit" loading={submitting}>
            {submitLabel}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function maybeError(message: string | undefined): { error: string } | Record<string, never> {
  return message === undefined ? {} : { error: message };
}
